import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { kingdoms } from "../data/kingdoms";
import { Atmosphere } from "../components/Atmosphere";
import { X, Flame, Droplets, Wind, Leaf, Sparkles } from "lucide-react";
import mapBgLocal from "./RuihuaKarte.png";
import landschaftsBild from "./Landschaftsbild.png";

export default function World() {
  const [active, setActive] = useState(null);

  // 1. Sprache aus dem Speicher holen & Event-Lauscher für die Navbar aufsetzen
  const [currentLang, setCurrentLang] = useState(() => {
    return localStorage.getItem("project_lang") || "de";
  });

  useEffect(() => {
    const handleLangChange = () => {
      setCurrentLang(localStorage.getItem("project_lang") || "de");
    };
    window.addEventListener("languageChange", handleLangChange);
    return () => window.removeEventListener("languageChange", handleLangChange);
  }, []);

  // 2. Elementarkräfte übersetzt
  const elements = [
    { 
      icon: Flame, 
      name: currentLang === "en" ? "Fire" : "Feuer", 
      color: "#B01218", 
      desc: currentLang === "en" 
        ? "The most dangerous and common power in Ruìhuá. Taught as the sole elemental art in Wūyúnfēng." 
        : "Gefährlichste und häufigste Kraft Ruìhuás. Wird als einzige Elementarkraft in Wūyúnfēng gelehrt." 
    },
    { 
      icon: Droplets, 
      name: currentLang === "en" ? "Water" : "Wasser", 
      color: "#2E78A8", 
      desc: currentLang === "en"
        ? "The second most powerful and common strength. Originally manifested in those living where the land meets the water."
        : "Zweitmächtigste und -häufigste Kraft. Betraf ursprünglich die Menschen, die da wohnen, wo das Land auf Wasser trifft." 
    },
    { 
      icon: Wind, 
      name: currentLang === "en" ? "Air" : "Luft", 
      color: "#E5C547", 
      desc: currentLang === "en"
        ? "A pure power from the west of the Central Empire. Today, it can only be found there."
        : "Reine Kraft aus dem Westen des Zentralen Reiches. Heute nur noch dort vorzufinden." 
    },
    { 
      icon: Leaf, 
      name: currentLang === "en" ? "Earth" : "Erde", 
      color: "#0F8B6D", 
      desc: currentLang === "en"
        ? "The strongest elemental power, yet went extinct with the fall of the Bukjiwon and Namjiwon kingdoms and the disappearance of their heirs."
        : "Stärkste Kraft, doch mit dem Fall der Reiche Bukjiwon und Namjiwon und dem Verschwinden der Thronfolger ausgestorben." 
    },
    { 
      icon: Sparkles, 
      name: "Elyra", 
      color: "#7A4A2A", 
      desc: currentLang === "en"
        ? "The force felt by every citizen of Ruìhuá. It connects the people and channels magical powers and feelings."
        : "Die Kraft, die alle Einwohner Ruìhuás spüren. Sie verbindet das Volk und überträgt Kräfte und Gefühle." 
    },
  ];

  // Helper-Funktion um verschachtelte kingdom-Objekte sicher aufzulösen
  const getLocalizedProp = (obj, prop) => {
    if (!obj || !obj[prop]) return "";
    return typeof obj[prop] === "object" ? (obj[prop][currentLang] || obj[prop]["de"]) : obj[prop];
  };

  return (
    <div className="pt-32 pb-24 px-6 relative" data-testid="world-page">
      <Atmosphere variant="minimal" />
      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-center mb-16"
        >
          <p className="chinese text-[#C9A227]/70 text-2xl mb-3">瑞华</p>
          <h1 className="serif text-5xl lg:text-7xl text-[#E8DDC8] mb-4">
            {currentLang === "en" ? (
              <>The World of <span className="italic text-[#C9A227]">Ruìhuá</span></>
            ) : (
              <>Die Welt von <span className="italic text-[#C9A227]">Ruìhuá</span></>
            )}
          </h1>
          <p className="serif italic text-[#B3B6B7] text-lg max-w-2xl mx-auto">
            {currentLang === "en" ? '"The river knows the way home."' : '"Der Fluss kennt den Weg nach Hause."'}
          </p>
        </motion.div>

        {/* MAP */}
        <div className="relative w-full aspect-[4096/2894] border border-[#C9A227]/30 overflow-hidden mb-16" data-testid="kingdom-map">
          <img src={mapBgLocal} alt="Map" className="absolute inset-0 w-full h-full object-cover" style={{ filter: "sepia(0.4) brightness(0.8)" }} />
          <div className="absolute inset-0 bg-gradient-to-br from-[#080808]/40 via-transparent to-[#080808]/60" />

          {kingdoms.map((k) => {
            const kName = getLocalizedProp(k, "name");
            return (
              <button
                key={k.id}
                onClick={() => setActive(k)}
                data-testid={`kingdom-pin-${k.id}`}
                className="absolute -translate-x-1/2 -translate-y-1/2 group"
                style={{ left: `${k.x}%`, top: `${k.y}%` }}
              >
                <span
                  className="block w-5 h-5 rounded-full transition-all group-hover:scale-150"
                  style={{
                    background: k.color,
                    boxShadow: `0 0 80px 25px ${k.color}88`,
                  }}
                />
                <span
                  className="absolute left-1/2 -translate-x-1/2 mt-1 whitespace-nowrap serif text-sm tracking-wider group-hover:opacity-100"
                  style={{ color: k.color }}
                >
                  {kName}
                </span>
              </button>
            );
          })}

          <div className="corner-orn tl" />
          <div className="corner-orn tr" />
          <div className="corner-orn bl" />
          <div className="corner-orn br" />
        </div>

        {/* Kingdom Detail Modal */}
        <AnimatePresence>
          {active && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[80] flex items-center justify-center px-4 bg-[#080808]/80 backdrop-blur"
              onClick={() => setActive(null)}
              data-testid="kingdom-modal"
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="relative max-w-xl w-full bg-[#0a0a0a] border border-[#C9A227]/30 p-10"
              >
                <button
                  onClick={() => setActive(null)}
                  className="absolute top-4 right-4 text-[#E8DDC8]"
                  data-testid="close-kingdom-modal"
                >
                  <X size={30} />
                </button>
                <div className="corner-orn tl" />
                <div className="corner-orn br" />
                <p className="chinese text-3xl mb-2" style={{ color: active.color }}>
                  {active.chinese}
                </p>
                <h3 className="serif text-4xl text-[#E8DDC8] mb-1">{getLocalizedProp(active, "name")}</h3>
                <p className="text-sm tracking-[0.3em] uppercase text-[#7A7C7D] mb-6">
                  {getLocalizedProp(active, "title")}
                </p>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase text-[#C9A227] mb-1">Merkmal</p>
                    <p className="text-[#E8DDC8]">{getLocalizedProp(active, "element")}</p>
                  </div>
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase text-[#C9A227] mb-1">
                      {currentLang === "en" ? "Nature" : "Wesen"}
                    </p>
                    <p className="text-[#B3B6B7] leading-relaxed">{getLocalizedProp(active, "description")}</p>
                  </div>
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase text-[#C9A227] mb-1">
                      {currentLang === "en" ? "Politics" : "Politik"}
                    </p>
                    <p className="text-[#B3B6B7] leading-relaxed italic">{getLocalizedProp(active, "politics")}</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Kingdoms grid */}
        <div className="mt-20">
          <div className="divider-gold mb-12" />
          <h2 className="serif text-4xl text-[#E8DDC8] mb-10 text-center">
            {currentLang === "en" ? "The Kingdoms" : "Die Königreiche"}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {kingdoms.map((k) => (
              <button
                key={k.id}
                onClick={() => setActive(k)}
                data-testid={`kingdom-card-${k.id}`}
                className="card-imperial p-7 text-left"
              >
                <p className="chinese text-2xl mb-2" style={{ color: k.color }}>{k.chinese}</p>
                <h3 className="serif text-2xl text-[#E8DDC8]">{getLocalizedProp(k, "name")}</h3>
                <p className="text-xs tracking-[0.2em] uppercase text-[#7A7C7D] mt-1 mb-3">{getLocalizedProp(k, "title")}</p>
                <p className="text-[#B3B6B7] text-sm leading-relaxed">{getLocalizedProp(k, "element")}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Elements */}
        <div className="mt-24">
          <div className="divider-gold mb-12" />
          <h2 className="serif text-4xl text-[#E8DDC8] mb-3 text-center">
            {currentLang === "en" ? "Elemental Powers" : "Elementarkräfte"}
          </h2>
          <p className="serif italic text-[#B3B6B7] text-center mb-12 max-w-2xl mx-auto">
            {currentLang === "en"
              ? "Four elemental forces shape the breath of Ruìhuá. Fire, water, air, earth. Special power mutations occur where pure forces cross paths."
              : "Vier Elementarkräfte formen den Atem Ruìhuás. Feuer, Wasser, Luft, Erde. Besondere Kräfte -Mutationen- entstehen, wo sich die reinen Kräfte kreuzen."}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {elements.map((el, i) => {
              const Icon = el.icon;
              return (
                <motion.div
                  key={el.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="card-imperial p-7"
                  data-testid={`element-card-${i}`}
                >
                  <Icon size={28} style={{ color: el.color }} />
                  <h3 className="serif text-xl text-[#E8DDC8] mt-4">{el.name}</h3>
                  <p className="text-sm text-[#B3B6B7] mt-2 leading-relaxed">{el.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* History / Dragons */}
        <div className="mt-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="chinese text-[#C9A227]/70 text-3xl mb-3">瑞华/西方</p>
            <h2 className="serif text-4xl text-[#E8DDC8] mb-6">
              {currentLang === "en" ? "History of Ruìhuá and Xīfāng" : "Geschichte von Ruìhuá und Xīfāng"}
            </h2>
            <p className="text-[#B3B6B7] leading-relaxed mb-4">
              {currentLang === "en" 
                ? "Danger has always loomed from Xīfāng. It is considered a lawless territory filled with thieves and bandits, though much of this consists of mere stories, forged out of hatred and fear of the unknown. Between both lands lies the formidable Gāobì mountain range, boasting only a few treacherous passes, which is why barely anyone knows the other side."
                : "Von Xīfāng geht seit jeher Gefahr aus. Xīfāng gilt als gesetzloses Gebiet voller Diebe und Banditen, doch vieles davon sind nur Geschichten, geschmiedet aus Hass und der Angst vor dem Unbekannten. Zwischen beiden Ländern liegt das schwer überwindbare Gebirge Gāobì mit nur wenigen Pässen, weshalb kaum jemand die andere Seite kennt."}
            </p>
            <p className="text-[#B3B6B7] leading-relaxed">
              {currentLang === "en"
                ? "In Xīfāng, rebels occasionally attempt to establish their own rule, but due to the sheer size, unforgiving location, and topography of the area, they usually fail quickly. For this reason, the imperial family drew the border right in front of the mountains and has guarded it strictly ever since, in order to protect the inhabitants living near the border."
                : "In Xīfāng versuchen zwar gelegentlich Rebellen, eigene Herrschaften zu errichten, doch wegen der Größe und ungünstigen Lage und Topografie des Gebietes scheitern sie meist schnell. Deshalb zog die Kaiserfamilie die Grenze direkt vor das Gebirge und bewacht sie seither streng, um die Bewohner nahe der Grenze zu schützen."}
            </p>
          </div>

          {/* Der Rahmen-Container steuert jetzt die Größe und zentriert sich */}
          <div className="relative w-4/5 max-w-md mx-auto aspect-square overflow-hidden border border-[#C9A227]/30">
            <img
              src={landschaftsBild}
              alt={currentLang === "en" ? "Imperial Landscape" : "Imperiale Landschaft"}
              className="w-full h-full object-cover" // Füllt den verkleinerten Rahmen jetzt wieder perfekt aus
              style={{ filter: "saturate(0.8) brightness(0.65)" }}
            />
            {/* Die Ornamente sitzen jetzt auch exakt auf den neuen, kleineren Ecken */}
            <div className="corner-orn tl" />
            <div className="corner-orn br" />
          </div>
        </div>
      </div>
    </div>
  );
}